/**
 * Session isolation for optional canonical calendar previews, and the binding that makes the two
 * planning Workers jobs of the page-wide compute scheduler. Client-selected placements are
 * submitted separately for server admission; this module performs no save and a computed
 * preview never implies persistence.
 *
 * Binding, not policy: every decision about when a planning job runs is made by the Rust compute
 * scheduler (`crates/compute-scheduler`). Solve and missed-activity rollover are `interactive`
 * jobs of the `planning` group, calendar suggestions are `visible` jobs of the `planning-ghosts`
 * group, and each group's warm-up is a deduplicated job of the same group, so energy training
 * (background) pauses while any of them runs and a request that arrives during a warm-up takes the
 * warm Worker. Workers come from warm pools that only ever park never-used Workers.
 *
 * Lifecycle guarantees are unchanged: logout, owner or token changes and page exit terminate both
 * Workers, discard any warm spare and cancel every planning job; a queued job from an old session
 * fails its fence before it can reach a Worker.
 * @module apps/web/src/lib/browserScheduling
 */
import { useUserStore } from '@/stores/userStore';
import { captureTaskSnapshot, isTaskSnapshotCurrent } from '@/stores/tasksStore';
import { SchedulingWorkerClient } from './schedulingWorkerClient';
import { getSchedulingSessionEpoch } from './browserScheduleFlow';
import {
  computeScheduler,
  createPoolPolicy,
  ComputeAbortError,
  WarmWorkerPool,
  type ComputeLane,
} from './compute';
import {
  parseSchedulingGhostBlocks,
  type SchedulingBackend,
  type SchedulingPreview,
  type SchedulingGhostPreview,
  type SchedulingWorkerReply,
  type SchedulingWorkerRequest,
  type SchedulingWorkerResponse,
} from './schedulingProtocol';

/** Identity captured at Worker creation and rechecked before accepting any result. */
interface BrowserSchedulingSession {
  /** Canonical signed-in owner whose private snapshot may enter the Worker. */
  userId: string;
  /** Opaque session token retained in the parent only; changes destroy the Worker. */
  token: string | null;
}

/** Private canonical preview with separately measured compute and transport boundaries. */
export interface BrowserSchedulingPreview {
  /** Provisional canonical documents; the caller extracts narrow placements for admission. */
  result: SchedulingPreview;
  /** WASM computation including JSON decode and encode, excluding initialization. */
  computeMs: number;
  /** Parent-thread post-to-reply latency including structured cloning. */
  roundTripMs: number;
  /** Actual local execution backend; GPU classifies calendar intervals only. */
  backend: 'wasm' | 'wasm-webgpu';
  /** GPU transport-inclusive computation, zero on the CPU path. */
  gpuMs: number;
}

/** Message shown for every lifecycle rejection, whichever path detected it. */
const SESSION_CHANGED = 'Your scheduling session changed.';

/** Construct the full-solve module Worker (bundled by Vite). */
function createPlanningWorker(): Worker {
  return new Worker(new URL('../workers/scheduling.worker.ts', import.meta.url), {
    type: 'module',
    name: 'fluxability-canonical-scheduling',
  });
}

/** Construct the calendar-suggestion module Worker (bundled by Vite). */
function createGhostWorker(): Worker {
  return new Worker(new URL('../workers/scheduling.worker.ts', import.meta.url), {
    type: 'module',
    name: 'fluxability-calendar-suggestions',
  });
}

/**
 * Load the canonical module in a pooled, never-used Worker with the existing `warm` message of
 * the scheduling protocol. Listens with `addEventListener`, so the client that later acquires the
 * Worker keeps sole ownership of `onmessage`. No user data is posted.
 */
function warmSchedulingWorker(worker: Worker, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = `warm:${crypto.randomUUID()}`;
    const done = () => {
      worker.removeEventListener('message', onMessage as EventListener);
      worker.removeEventListener('error', onError);
      signal.removeEventListener('abort', onAbort);
    };
    const onMessage = (event: MessageEvent<SchedulingWorkerReply>) => {
      if (event.data?.id !== id) return;
      done();
      if (event.data.error) reject(new Error(event.data.error));
      else resolve();
    };
    const onError = () => {
      done();
      reject(new Error('Browser scheduling Worker failed during warm-up.'));
    };
    const onAbort = () => {
      done();
      reject(signal.reason ?? new Error('Browser scheduling warm-up cancelled.'));
    };
    if (signal.aborted) return onAbort();
    worker.addEventListener('message', onMessage as EventListener);
    worker.addEventListener('error', onError);
    signal.addEventListener('abort', onAbort, { once: true });
    worker.postMessage({ id, kind: 'warm', backend: 'auto' } satisfies SchedulingWorkerRequest);
  });
}

/** At most one warm, never-used full-solve Worker (Rust `pool` policy). */
const planningPool = new WarmWorkerPool(createPlanningWorker, {
  warm: warmSchedulingWorker,
  policy: createPoolPolicy,
});
/** At most one warm, never-used suggestion Worker (Rust `pool` policy). */
const ghostPool = new WarmWorkerPool(createGhostWorker, {
  warm: warmSchedulingWorker,
  policy: createPoolPolicy,
});

/** Singleton holds only the current session's in-memory Worker and pending requests. */
const client = new SchedulingWorkerClient(() => planningPool.acquire());
/** Ghost requests have their own private WASM instance: stopping a refresh never stops Solve. */
const ghostClient = new SchedulingWorkerClient(() => ghostPool.acquire());

/** One full solve or rollover request as the scheduler carries it. */
interface PlanningRequest {
  /** Authorized snapshot JSON. */
  input: string;
  /** Requested local backend. */
  backend: SchedulingBackend;
  /** Owner check run when the job starts, before the snapshot reaches a Worker. */
  fence: () => void;
}

/** One calendar-suggestion request as the scheduler carries it. */
interface GhostRequest {
  /** Authorized snapshot JSON. */
  input: string;
  /** Exact server-selected week. */
  weekStart: string;
  /** Owner, logout, revision and abort checks run when the job starts. */
  fence: () => void;
}

/** Solve and rollover: `interactive` jobs, one at a time on the full-solve Worker. */
const planningCompute = computeScheduler.register<PlanningRequest, SchedulingWorkerResponse>({
  name: 'planning',
  lane: 'interactive',
  maxConcurrency: 1,
  execute: (request, signal) => {
    request.fence();
    return client.solve(request.input, signal, request.backend);
  },
  prewarm: signal => planningPool.prewarm(signal),
});

/** Calendar suggestions: `visible` jobs, one at a time on their own Worker. */
const ghostCompute = computeScheduler.register<GhostRequest, SchedulingWorkerResponse>({
  name: 'planning-ghosts',
  lane: 'visible',
  maxConcurrency: 1,
  execute: (request, signal) => {
    request.fence();
    return ghostClient.ghosts(request.input, request.weekStart, signal);
  },
  prewarm: signal => ghostPool.prewarm(signal),
});

/** Bound queued snapshot retention when several calendar consumers refresh together. */
let queuedGhosts = 0;
/** Owner and token captured at Worker creation; tokens are never posted to the Worker. */
let session: BrowserSchedulingSession | undefined;

/** Reset immediately when identity changes, including logout in the same browser tab. */
useUserStore.subscribe(state => {
  if (
    session &&
    (state.userId !== session.userId ||
      localStorage.getItem('flexiplanner-session-token') !== session.token)
  )
    resetBrowserScheduling();
});

/** Drop private snapshots and WASM memory when a token changes in another browser tab. */
if (typeof window !== 'undefined')
  window.addEventListener('storage', event => {
    if (event.key === null || event.key === 'flexiplanner-session-token') resetBrowserScheduling();
  });

/** Page exit erases private WASM memory even if the document enters the back-forward cache. */
if (typeof window !== 'undefined') window.addEventListener('pagehide', resetBrowserScheduling);

/**
 * Explicitly erase the session-owned Workers, reject incomplete previews, cancel queued planning
 * jobs and warm-ups, and discard warm spares, so no Worker of the old session survives.
 */
export function resetBrowserScheduling(): void {
  session = undefined;
  client.reset(SESSION_CHANGED);
  ghostClient.reset(SESSION_CHANGED);
  planningCompute.cancelAll();
  ghostCompute.cancelAll();
  planningPool.clear();
  ghostPool.clear();
}

/** Report a scheduler reset (logout while queued or running) with the lifecycle message. */
function sessionError(error: unknown): unknown {
  return error instanceof ComputeAbortError && error.reason === 'reset'
    ? new Error(SESSION_CHANGED)
    : error;
}

/** Reject stale ghost work without destroying another owner's newly initialized full-solve Worker. */
function assertSchedulingOwner(owner: BrowserSchedulingSession): void {
  if (session !== owner) throw new Error(SESSION_CHANGED);
  if (
    useUserStore.getState().userId !== owner.userId ||
    localStorage.getItem('flexiplanner-session-token') !== owner.token
  ) {
    resetBrowserScheduling();
    throw new Error(SESSION_CHANGED);
  }
}

/** Run the unchanged soft-capacity ghost algorithm locally, preserving full result metadata and every lifecycle fence. */
export async function previewBrowserGhosts(
  input: unknown,
  weekStart: string,
  signal?: AbortSignal
): Promise<SchedulingGhostPreview> {
  const owner = currentSession();
  const epoch = getSchedulingSessionEpoch();
  const taskSnapshot = captureTaskSnapshot();
  const snapshot = input as {
    /** Authorized snapshot owner. */ user_id?: string;
    /** Version supported by the shared Rust runtime. */ version?: number;
    /** Snapshot correlation identity. */ job_id?: string;
    /** Canonical activity revision. */ revision?: number;
  };
  if (
    snapshot?.user_id !== owner.userId ||
    snapshot?.version !== 1 ||
    !snapshot.job_id ||
    !Number.isFinite(snapshot.revision)
  )
    throw new Error('Calendar suggestions belong to another snapshot.');
  if (queuedGhosts >= 4) throw new Error('Calendar suggestions are already refreshing.');
  /** Check again when the job starts and after compute; logout intent precedes token removal. */
  const fence = () => {
    signal?.throwIfAborted();
    assertSchedulingOwner(owner);
    if (epoch !== getSchedulingSessionEpoch()) throw new Error(SESSION_CHANGED);
    if (!isTaskSnapshotCurrent(taskSnapshot))
      throw new Error('Activities changed while preparing suggestions. Refresh the calendar.');
  };
  queuedGhosts += 1;
  try {
    const reply = await ghostCompute
      .submit({ input: JSON.stringify(input), weekStart, fence }, { signal })
      .catch(error => {
        throw sessionError(error);
      });
    fence();
    if (
      !reply.ghosts ||
      reply.ghosts.job_id !== snapshot.job_id ||
      reply.ghosts.revision !== snapshot.revision ||
      reply.ghosts.week_start !== weekStart
    )
      throw new Error('Calendar suggestion provenance mismatch.');
    parseSchedulingGhostBlocks(reply.ghosts.blocks);
    return reply.ghosts;
  } finally {
    queuedGhosts -= 1;
  }
}

/** Require the current owner and token before accessing a persistent Worker. */
function currentSession(): BrowserSchedulingSession {
  const userId = useUserStore.getState().userId;
  if (!userId) throw new Error('Sign in before scheduling.');
  const token = localStorage.getItem('flexiplanner-session-token');
  if (!session || session.userId !== userId || session.token !== token) {
    resetBrowserScheduling();
    session = { userId, token };
  }
  return session;
}

/**
 * Ask for a warm full-solve Worker before the person clicks, without user data: `visible` when
 * the calendar mounts, `interactive` when a pointer or focus rests on Solve (which joins and
 * promotes a warm-up already queued or running). The Rust scheduler and pool policy decide
 * whether anything starts; nothing does while the session's Worker is alive. Older clients
 * without Worker support retain server scheduling.
 */
export function warmBrowserScheduling(lane: ComputeLane = 'visible'): void {
  if (typeof Worker === 'undefined' || !useUserStore.getState().userId) return;
  currentSession();
  // Without the Rust core there is no pool policy; warm the session Worker directly, as before.
  if (computeScheduler.status === 'passthrough') {
    void client.warm().catch(() => undefined);
    return;
  }
  planningCompute.prewarm({ lane });
}

/**
 * Warm the suggestion Worker while the calendar's authorized read is still on the network, so
 * Worker start-up overlaps the HTTP round trip instead of following it. No user data is posted.
 */
export function warmBrowserGhosts(): void {
  if (typeof Worker === 'undefined' || !useUserStore.getState().userId) return;
  if (computeScheduler.status === 'passthrough') return;
  currentSession();
  ghostCompute.prewarm({ lane: 'visible' });
}

/** Compute one exact authorized snapshot, rejecting results after token/owner/job changes. */
export async function previewBrowserSchedule(
  input: unknown,
  signal?: AbortSignal,
  backend: SchedulingBackend = 'auto'
): Promise<BrowserSchedulingPreview> {
  const owner = currentSession();
  const snapshot = input as {
    user_id?: string;
    job_id?: string;
    revision?: number;
    version?: number;
  };
  if (snapshot.user_id !== owner.userId || snapshot.version !== 1)
    throw new Error('Scheduling snapshot belongs to another session.');
  /** A job queued under an earlier session never reaches a Worker. */
  const fence = () => {
    if (session !== owner) throw new Error(SESSION_CHANGED);
  };
  const reply = await planningCompute
    .submit({ input: JSON.stringify(input), backend, fence }, { signal })
    .catch(error => {
      throw sessionError(error);
    });
  if (
    session !== owner ||
    useUserStore.getState().userId !== owner.userId ||
    localStorage.getItem('flexiplanner-session-token') !== owner.token ||
    signal?.aborted
  ) {
    resetBrowserScheduling();
    throw new Error(SESSION_CHANGED);
  }
  if (
    !reply.result ||
    reply.result.job_id !== snapshot.job_id ||
    reply.result.revision !== snapshot.revision
  )
    throw new Error('Scheduling preview provenance mismatch.');
  return {
    result: reply.result,
    computeMs: reply.computeMs ?? 0,
    roundTripMs: reply.roundTripMs,
    backend: reply.backend ?? 'wasm',
    gpuMs: reply.gpuMs ?? 0,
  };
}
